import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import type { INavigationGraph, NavGraphFormValues } from '~/types/navigation-graph'

import {
  createNavigationGraph,
  getNavigationGraph,
  getNavigationGraphs,
  updateNavigationGraph,
} from '~/api/navigation-graphs'

////////// KEYS //////////
export const navigationGraphsKeys = {
  all: ['navigation-graphs'] as const,
  details: (navGraphId?: string) => [...navigationGraphsKeys.all, 'details', navGraphId] as const,
  list: (params?: AxiosRequestConfig['params']) => [...navigationGraphsKeys.all, 'list', params] as const,
}

////////// QUERIES //////////
export const useNavigationGraphsListQuery = (
  params?: AxiosRequestConfig['params'],
  settings?: { enabled: boolean }
): UseQueryResult<IPaginatedResponse<INavigationGraph>> => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => getNavigationGraphs(params),
    queryKey: navigationGraphsKeys.list(params),
    ...settings,
  })
}

export const useNavigationGraphDetailsQuery = (navGraphId?: string): UseQueryResult<INavigationGraph> => {
  return useQuery({
    enabled: !!navGraphId && navGraphId !== 'creation',
    queryFn: () => getNavigationGraph(navGraphId),
    queryKey: navigationGraphsKeys.details(navGraphId),
  })
}

////////// MUTATIONS //////////
export const useCreateNavigationGraphMutation = (): UseMutationResult<
  INavigationGraph,
  unknown,
  { data: NavGraphFormValues },
  unknown
> => {
  return useMutation({ mutationFn: createNavigationGraph })
}

export const useUpdateNavigationGraphMutation = (): UseMutationResult<
  void,
  unknown,
  {
    data: NavGraphFormValues
    navGraphId?: string
  },
  unknown
> => {
  return useMutation({ mutationFn: updateNavigationGraph })
}
